import React, { useCallback, useEffect, useState } from "react";
import { DiscDetail } from "@/components/erp/DiscDetail";
import { api, formatIDR, hasRole } from "@/lib/api";
import { PageHeader, Card, Btn } from "@/components/erp/ui";
import { toast } from "sonner";
import { Printer, Download, Gauge } from "lucide-react";
import { PerfChart } from "@/components/erp/PerfChart";
import { useAuth } from "@/context/AuthContext";

const Stat = ({ label, value, tone = "", testid, onClick }) => (
  <div onClick={onClick} data-testid={testid ? `${testid}-open` : undefined} title="Klik untuk lihat rincian" className="p-3 rounded-lg bg-slate-50 border border-slate-100 cursor-pointer hover:border-sky-300 hover:bg-sky-50 transition-colors">
    <div className="text-[11px] font-mono uppercase text-slate-400">{label}</div>
    <div data-testid={testid} className={`text-lg font-semibold ${tone}`}>{value}</div>
  </div>
);

const MyScore = ({ me }) => {
  const e = me?.employee;
  const [metric, setMetric] = useState(null);
  const load = useCallback((m) => api.get(`/discipline/me?month=${m}`).then(({ data }) => data.employee || false), []);
  if (!e) return <Card className="p-5 mb-6 text-sm text-slate-500" data-testid="my-score-empty">Akun Anda belum terhubung ke data karyawan (email karyawan berbeda), jadi nilai performa belum bisa ditampilkan.</Card>;
  const tone = e.perf_low ? "text-rose-600" : e.perf < 80 ? "text-amber-600" : "text-emerald-600";
  return (
    <Card className="p-5 mb-6" data-testid="my-score">
      <div className="flex items-center gap-3 mb-4">
        <Gauge className={`w-8 h-8 ${tone}`} />
        <div>
          <div className="text-xs text-slate-500">Nilai Performa Anda · {me.month}</div>
          <div data-testid="my-perf" onClick={() => setMetric("perf")} title="Klik untuk lihat rincian" className={`text-3xl font-bold cursor-pointer hover:underline ${tone}`}>{e.perf}<span className="text-sm text-slate-400"> / {me.rules.perf_start}</span></div>
        </div>
        {e.perf_low && <span className="ml-auto text-xs bg-rose-100 text-rose-700 rounded-full px-3 py-1">Di bawah {me.rules.perf_min}: {me.rules.perf_low_action}</span>}
        {e.exempt && <span className="ml-auto text-xs bg-sky-100 text-sky-700 rounded-full px-3 py-1">Role Anda dikecualikan dari potongan</span>}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Telat masuk" value={`${e.late_days}x · ${e.net_late_min} mnt`} testid="my-late" onClick={() => setMetric("late")} />
        <Stat label="Lembur (pengurang)" value={`${e.overtime_min} mnt`} testid="my-ot" onClick={() => setMetric("ot")} />
        <Stat label="Tahap WO telat" value={`${e.wait_late} / ${e.stages}`} testid="my-wait-late" onClick={() => setMetric("wait")} />
        <Stat label="Istirahat" value={`${e.break_pct}%`} testid="my-break" onClick={() => setMetric("break")} />
        <Stat label="Potongan" value={formatIDR(e.penalty)} tone="text-rose-600" testid="my-penalty" onClick={() => setMetric("penalty")} />
        <Stat label="Bonus disiplin" value={formatIDR(e.disc_bonus)} tone="text-emerald-600" testid="my-bonus" onClick={() => setMetric("bonus")} />
        <div className="col-span-2 p-3 rounded-lg bg-slate-50 border border-slate-100 text-xs text-slate-600">{e.bonus_eligible ? "Memenuhi syarat bonus bulan ini." : `Belum memenuhi syarat bonus: ${e.bonus_notes.join("; ")}`}</div>
      </div>
      <div className="text-[11px] text-slate-400 mt-2">Klik angka mana saja untuk melihat asal nilainya dan riwayat per bulan.</div>
      <DiscDetail open={!!metric} onClose={() => setMetric(null)} metric={metric} setMetric={setMetric} load={load} name={e.name} month0={me.month} />
    </Card>
  );
};

const Changes = () => {
  const [rows, setRows] = useState([]); const [ack, setAck] = useState(null);
  const loadAck = () => api.get("/office-rules/ack-status").then(({ data }) => setAck(data)).catch(() => {});
  useEffect(() => { api.get("/office-rules/changes").then(({ data }) => setRows(data)).catch(() => {}); loadAck(); }, []);
  const read = async () => { try { await api.post("/office-rules/ack"); toast.success("Terima kasih, sudah tercatat membaca"); loadAck(); } catch (e) { toast.error("Gagal"); } };
  if (!rows.length) return null;
  return (
    <Card className="p-4 mb-4" data-testid="rules-changes">
      {ack?.need_ack && <div className="flex flex-wrap items-center gap-2 mb-2 p-2 rounded-lg bg-amber-50 border border-amber-200" data-testid="rules-need-ack"><span className="text-xs flex-1">Ada perubahan peraturan yang belum Anda baca.</span><Btn data-testid="rules-ack-btn" onClick={read}>Sudah saya baca</Btn></div>}
      {ack && !ack.need_ack && ack.my_ack_at && <div className="text-[11px] text-emerald-700 mb-1" data-testid="rules-acked">Anda sudah membaca peraturan terbaru ({new Date(ack.my_ack_at).toLocaleString("id-ID")}).</div>}
      {ack?.unread && <div className="text-xs mb-2" data-testid="rules-unread"><b className="text-rose-600">Belum membaca ({ack.unread.length}):</b> {ack.unread.join(", ") || "—"} <span className="text-slate-400">· sudah: {ack.read.length}</span></div>}
      <b className="text-sm">Riwayat perubahan aturan</b>
      <div className="max-h-60 overflow-y-auto mt-1">{rows.map((c) => <div key={c.id} className="flex flex-wrap gap-2 text-xs border-t border-slate-100 py-1" data-testid={`rules-change-${c.id}`}><span className="w-32 text-slate-400">{new Date(c.at).toLocaleString("id-ID")}</span><span className="flex-1">{c.label}: <s className="text-slate-400">{String(c.old)}</s> → <b>{String(c.new)}</b></span><span className="text-slate-500">oleh {c.by}</span></div>)}</div>
    </Card>
  );
};

export default function OfficeRules() {
  const [doc, setDoc] = useState(null);
  const [me, setMe] = useState(null);
  const [groups, setGroups] = useState([]);
  const [gid, setGid] = useState("mine");
  const { user } = useAuth();
  const mgr = hasRole(user, "owner", "hr");
  useEffect(() => { api.get(`/office-rules/doc?group_id=${gid}`).then(({ data }) => setDoc(data)).catch(() => {}); }, [gid]);
  useEffect(() => {
    api.get("/discipline/me").then(({ data }) => setMe(data)).catch(() => {});
    api.get("/office-groups").then(({ data }) => setGroups(data)).catch(() => {});
  }, []);
  const pdf = async () => {
    try {
      const res = await api.get(`/office-rules/pdf?group_id=${doc?.group_id || ""}`, { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      a.download = "peraturan_kantor.pdf"; a.click();
    } catch { toast.error("Gagal mengunduh PDF"); }
  };
  const print = () => {
    const el = document.getElementById("office-rules-doc");
    if (!el) return;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Izinkan pop-up untuk mencetak"); return; }
    w.document.write(`<html><head><title>Peraturan Kantor</title><style>body{font-family:Georgia,serif;padding:32px;color:#111}h1{margin:0}h3{margin-top:18px}li{margin:4px 0}.sig{margin-top:48px;display:flex;justify-content:space-between}</style></head><body>${el.innerHTML}<div class="sig"><span>Nama: ____________</span><span>Tanggal: ________</span><span>Tanda tangan: ________</span></div></body></html>`);
    w.document.close(); w.focus(); w.print();
  };
  return (
    <div>
      <PageHeader title="Peraturan Kantor" subtitle="Jam kerja, aturan telat, nilai performa dan bonus. Bisa dicetak untuk karyawan baru."
        action={<div className="flex gap-2"><Btn variant="ghost" data-testid="rules-print-btn" onClick={print}><Printer className="w-4 h-4" /> Cetak</Btn><Btn data-testid="rules-pdf-btn" onClick={pdf}><Download className="w-4 h-4" /> PDF</Btn></div>} />
      <MyScore me={me} />
      <Changes />
      <PerfChart />
      {mgr && groups.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid="rules-group-select">
          {[{ id: "mine", name: "Grup saya" }, { id: "", name: "Aturan Umum" }, ...groups].map((g) => <button key={g.id || "base"} data-testid={`rules-group-${g.id || "base"}`} onClick={() => setGid(g.id)} className={`px-3 py-1 rounded-full text-xs ${gid === g.id ? "bg-slate-900 text-white" : "border border-slate-300"}`}>{g.name}</button>)}
        </div>
      )}
      {doc && (
        <Card className="p-6 sm:p-8">
          <div id="office-rules-doc" data-testid="office-rules-doc">
            <h1 className="text-2xl font-bold">{doc.company}</h1>
            <p className="text-sm text-slate-500 mb-4">Peraturan Kantor — {doc.group}</p>
            {doc.sections.map((s, i) => (
              <div key={s.title} className="mb-4" data-testid={`rules-section-${i}`}>
                <h3 className="font-semibold">{i + 1}. {s.title}</h3>
                <ul className="list-disc pl-6 text-sm text-slate-700 space-y-1 mt-1">{s.items.map((x, j) => <li key={j}>{x}</li>)}</ul>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
